import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const BROKER_ROLES = [
  'Broker',
  'Associate Broker',
  'Principal',
  'Agent',
  'Transaction Coordinator',
  'Other',
];

const BrokersGetStartedForm: React.FC<{ id?: string }> = ({ id }) => {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [brokerageName, setBrokerageName] = useState('');
  const [brokerageCity, setBrokerageCity] = useState('');
  const [brokerageRole, setBrokerageRole] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: wire to backend / lead capture
  };

  return (
    <form onSubmit={handleSubmit} id={id} className="space-y-4">
      <h3 className="text-xl font-semibold text-[#0c4a6e]">Get Started with BrokerWorks</h3>
      <p className="text-sm text-gray-500">All fields required</p>
      <div className="space-y-3">
        <input
          type="text"
          placeholder="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          className="w-full px-3 py-2.5 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        <input
          type="tel"
          placeholder="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          className="w-full px-3 py-2.5 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        <input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-3 py-2.5 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        <input
          type="text"
          placeholder="Brokerage Name"
          value={brokerageName}
          onChange={(e) => setBrokerageName(e.target.value)}
          required
          className="w-full px-3 py-2.5 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        <input
          type="text"
          placeholder="Brokerage City"
          value={brokerageCity}
          onChange={(e) => setBrokerageCity(e.target.value)}
          required
          className="w-full px-3 py-2.5 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        <select
          value={brokerageRole}
          onChange={(e) => setBrokerageRole(e.target.value)}
          required
          className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27%3e%3cpath d=%27M6 9l6 6 6-6%27/%3e%3c/svg%3e')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10"
        >
          <option value="">Brokerage Role</option>
          {BROKER_ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>
      <button
        type="submit"
        className="w-full py-3 rounded-md bg-secondary-500 hover:bg-secondary-600 text-white font-medium text-sm transition-colors"
      >
        Get Started
      </button>
    </form>
  );
};

const BrokersLanding: React.FC = () => {
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  usePageTitle('BrokerWorks for Business Brokers | BizBuyMax');

  const testimonials = [
    {
      quote:
        "In the past 2.5 years, we calculated that our firm's use of BizBuyMax has brought a 3,637% ROI. We list businesses on at least 5-7 different platforms but most of our buyers end up coming from BizBuyMax. In our last 4 transactions, we found that 82% of the LOIs we received were from buyers that found the listing from BizBuyMax. We love the pricing and accessibility of the platform for both buyers and sellers, which is why it's such a frontrunner in the space. Thanks BizBuyMax!",
      author: 'Christine McDonnell, The Magnolia Firm, California',
      // Placeholder for profile image - use a stock or leave as div
    },
  ];

  const benefits = [
    '3.5 million visits from buyers & sellers each month',
    'Robust sold and for-sale business comparables',
    'Listing activity insights to monitor the market',
    '25+ years as business-for-sale industry leaders',
    'Exposure on the top business & CRE marketplaces',
  ];

  return (
    <>
      <Helmet>
        <title>BrokerWorks for Business Brokers | BizBuyMax</title>
        <meta name="description" content="BrokerWorks by BizBuyMax — the industry standard for business brokers. Get more exposure, comparables, and tools to close more deals." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Section 1: Hero */}
        <section
          className="relative min-h-[420px] flex flex-col justify-end pb-8 pt-16 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(12, 74, 110, 0.45), rgba(12, 74, 110, 0.45)), url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80)`,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2">BrokerWorks</h1>
            <p className="text-lg sm:text-xl text-white/95 mb-8">The Industry Standard for Business Brokers</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
              <div className="bg-white/95 backdrop-blur rounded-lg border border-gray-200 p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 text-base mb-1">The Best Service</h3>
                <p className="text-sm text-gray-600">Reach the largest audience of qualified buyers and sellers in the business-for-sale space.</p>
              </div>
              <div className="bg-white/95 backdrop-blur rounded-lg border border-gray-200 p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 text-base mb-1">The Most Popular</h3>
                <p className="text-sm text-gray-600">99% of all business brokers use BizBuyMax.</p>
              </div>
              <div className="bg-white/95 backdrop-blur rounded-lg border border-gray-200 p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 text-base mb-1">The Most Exposure</h3>
                <p className="text-sm text-gray-600">#1 business-for-sale marketplace. Your listings get maximum visibility.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Only BrokerWorks Delivers + Form */}
        <section className="bg-white py-14 sm:py-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#0c4a6e] mb-6">Only BrokerWorks Delivers:</h2>
                <ul className="space-y-3">
                  {benefits.map((text, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircleIcon className="w-6 h-6 text-primary-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-base">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm max-w-md lg:max-w-none lg:ml-auto">
                <BrokersGetStartedForm />
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: One Network. Millions of Buyers. */}
        <section className="py-14 sm:py-18" style={{ backgroundColor: '#e8eef2' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0c4a6e] mb-3">One Network. Millions of Buyers.</h2>
            <p className="text-gray-600 text-base mb-12">
              Manage your listings on BizBuyMax for instant exposure to our entire network of sites.
            </p>
            {/* Network diagram: BizBuyMax -> BizQuest, LoopNet -> DealStream, The Business Observer, WSI */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-11 h-11 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">B</span>
                </div>
                <span className="font-semibold text-[#0c4a6e] text-lg">BizBuyMax</span>
              </div>
              <div className="w-0 h-8 border-l border-dashed border-gray-400 my-1" />
              <div className="flex items-center justify-center gap-12 sm:gap-20">
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-primary-400 flex-shrink-0" />
                    <span className="font-medium text-[#0c4a6e]">BizQuest</span>
                  </div>
                  <div className="w-0 h-7 border-l border-dashed border-gray-400 my-0.5" />
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                    <span>DealStream</span>
                    <span>The Business Observer</span>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-secondary-400 flex-shrink-0" />
                    <span className="font-medium text-[#0c4a6e]">LoopNet</span>
                  </div>
                  <div className="w-0 h-7 border-l border-dashed border-gray-400 my-0.5" />
                  <div className="mt-1 text-sm text-gray-600">WSI</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: The Smarter Way to Reach Buyers & Sellers */}
        <section className="bg-white py-14 sm:py-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0c4a6e] text-center mb-12">
              The Smarter Way to Reach Buyers & Sellers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center text-primary-500 mb-4">
                  <DocumentTextIcon className="w-14 h-14" strokeWidth={1.25} />
                </div>
                <h3 className="font-semibold text-[#0c4a6e] text-lg">Manage Your Listings</h3>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center text-primary-500 mb-4">
                  <ChartBarIcon className="w-14 h-14" strokeWidth={1.25} />
                </div>
                <h3 className="font-semibold text-[#0c4a6e] text-lg">Track Performance</h3>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center text-primary-500 mb-4">
                  <UserGroupIcon className="w-14 h-14" strokeWidth={1.25} />
                </div>
                <h3 className="font-semibold text-[#0c4a6e] text-lg">Market Your Services</h3>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: What our members say */}
        <section className="py-14 sm:py-18" style={{ backgroundColor: '#e8eef2' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0c4a6e] mb-8">What our members say:</h2>
            <div className="flex items-start gap-4 sm:gap-8">
              <button
                type="button"
                onClick={() => setTestimonialIndex((i) => (i - 1 + testimonials.length) % testimonials.length)}
                className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-gray-400 text-gray-600 hover:bg-gray-200 flex items-center justify-center mt-4 transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0 flex gap-6 items-start">
                <div className="flex-1 min-w-0">
                  <blockquote className="text-[#0c4a6e] text-base leading-relaxed mb-4">
                    &ldquo;{testimonials[testimonialIndex].quote}&rdquo;
                  </blockquote>
                  <cite className="not-italic text-[#0c4a6e] text-sm font-medium">
                    {testimonials[testimonialIndex].author}
                  </cite>
                </div>
                <div className="hidden sm:block w-20 h-20 rounded-full bg-gray-300 flex-shrink-0 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=160&h=160&fit=crop&q=80"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTestimonialIndex((i) => (i + 1) % testimonials.length)}
                className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-gray-400 text-gray-600 hover:bg-gray-200 flex items-center justify-center mt-4 transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
            <a
              href="#more-stories"
              className="inline-flex items-center gap-1 text-[#0c4a6e] text-sm font-medium mt-6 underline hover:no-underline"
            >
              More client success stories
              <ArrowRightIcon className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Section 6: Bottom form + image */}
        <section className="bg-white py-14 sm:py-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="max-w-md">
                <BrokersGetStartedForm id="brokers-form-bottom" />
              </div>
              <div className="relative aspect-[4/3] max-h-[400px] rounded-lg overflow-hidden bg-gray-100">
                <img
                  src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80"
                  alt="Professional at laptop and phone"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BrokersLanding;
